import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const staffApi = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Handle session expiry — auth is carried by the httpOnly cookie
staffApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('staff_user');
      window.location.href = '/staff/login';
    }
    return Promise.reject(error);
  }
);

export const staffService = {
  // Dashboard
  async getDashboard() {
    const response = await staffApi.get('/api/staff/dashboard');
    return response.data;
  },

  // Categories
  async getCategories() {
    const response = await staffApi.get('/api/staff/categories');
    return response.data;
  },

  async createCategory(data) {
    const response = await staffApi.post('/api/staff/categories', data);
    return response.data;
  },

  async updateCategory(id, data) {
    const response = await staffApi.put(`/api/staff/categories/${id}`, data);
    return response.data;
  },

  async deleteCategory(id) {
    const response = await staffApi.delete(`/api/staff/categories/${id}`);
    return response.data;
  },

  // Inventory
  async getInventory(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.low_stock_only) params.append('low_stock_only', 'true');
    const response = await staffApi.get(`/api/staff/inventory?${params}`);
    return response.data;
  },

  async createIngredient(data) {
    const response = await staffApi.post('/api/staff/inventory', data);
    return response.data;
  },

  async updateIngredient(id, data) {
    const response = await staffApi.put(`/api/staff/inventory/${id}`, data);
    return response.data;
  },

  async deleteIngredient(id) {
    const response = await staffApi.delete(`/api/staff/inventory/${id}`);
    return response.data;
  },

  async adjustInventory(data) {
    const response = await staffApi.post('/api/staff/inventory/adjust', data);
    return response.data;
  },

  async getInventoryLog(ingredientId = null) {
    const params = ingredientId ? `?ingredient_id=${ingredientId}` : '';
    const response = await staffApi.get(`/api/staff/inventory/log${params}`);
    return response.data;
  },

  // Recipes
  async getRecipes(filters = {}) {
    const params = new URLSearchParams();
    if (filters.category) params.append('category', filters.category);
    if (filters.active_only !== undefined) params.append('active_only', filters.active_only);
    const response = await staffApi.get(`/api/staff/recipes?${params}`);
    return response.data;
  },

  async getRecipe(id) {
    const response = await staffApi.get(`/api/staff/recipes/${id}`);
    return response.data;
  },

  async createRecipe(data) {
    const response = await staffApi.post('/api/staff/recipes', data);
    return response.data;
  },

  async updateRecipe(id, data) {
    const response = await staffApi.put(`/api/staff/recipes/${id}`, data);
    return response.data;
  },

  async deleteRecipe(id) {
    const response = await staffApi.delete(`/api/staff/recipes/${id}`);
    return response.data;
  },

  async scaleRecipe(id, targetQuantity) {
    const response = await staffApi.get(`/api/staff/recipes/${id}/scale?target_quantity=${targetQuantity}`);
    return response.data;
  },

  // Production
  async getProductionBatches(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.status) params.append('status', filters.status);
    const response = await staffApi.get(`/api/staff/production?${params}`);
    return response.data;
  },

  async createProductionBatch(data) {
    const response = await staffApi.post('/api/staff/production', data);
    return response.data;
  },

  async generateProductionList(date) {
    const response = await staffApi.post(`/api/staff/production/generate?date=${date}`);
    return response.data;
  },

  // Update order within batch (new order-centric approach)
  async updateProductionOrder(batchId, orderIndex, data) {
    const response = await staffApi.put(`/api/staff/production/${batchId}/order/${orderIndex}`, data);
    return response.data;
  },

  // Legacy - update item directly
  async updateProductionItem(batchId, itemIndex, data) {
    const response = await staffApi.put(`/api/staff/production/${batchId}/item/${itemIndex}`, data);
    return response.data;
  },

  async completeProductionBatch(batchId) {
    const response = await staffApi.put(`/api/staff/production/${batchId}/complete`);
    return response.data;
  },

  async deleteProductionBatch(batchId) {
    const response = await staffApi.delete(`/api/staff/production/${batchId}`);
    return response.data;
  },

  async previewBatchResync(batchId) {
    const response = await staffApi.get(`/api/staff/production/${batchId}/resync/preview`);
    return response.data;
  },

  async resyncBatch(batchId, addOrderIds = []) {
    const response = await staffApi.post(`/api/staff/production/${batchId}/resync`, { add_order_ids: addOrderIds });
    return response.data;
  },

  async updateBatchStatus(batchId, status) {
    const response = await staffApi.put(`/api/staff/production/${batchId}/status?status=${status}`);
    return response.data;
  },

  // Custom Orders
  async createCustomOrder(data) {
    const response = await staffApi.post('/api/staff/custom-orders', data);
    return response.data;
  },

  async getCustomOrders(date = null, status = null) {
    const params = new URLSearchParams();
    if (date) params.set('date', date);
    if (status) params.set('status', status);
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await staffApi.get(`/api/staff/custom-orders${qs}`);
    return response.data;
  },

  async getCustomOrder(orderId) {
    const response = await staffApi.get(`/api/staff/custom-orders/${orderId}`);
    return response.data;
  },

  async updateCustomOrder(orderId, data) {
    const response = await staffApi.put(`/api/staff/custom-orders/${orderId}`, data);
    return response.data;
  },

  async updateCustomOrderStatus(orderId, status) {
    const response = await staffApi.patch(`/api/staff/custom-orders/${orderId}/status`, { status });
    return response.data;
  },

  async deleteCustomOrder(orderId) {
    const response = await staffApi.delete(`/api/staff/custom-orders/${orderId}`);
    return response.data;
  },

  // Forecast
  async getProductionForecast(startDate, endDate) {
    const response = await staffApi.get(`/api/staff/production/forecast?start_date=${startDate}&end_date=${endDate}`);
    return response.data;
  },

  async getProductionDayDetail(date) {
    const response = await staffApi.get(`/api/staff/production/day-detail?date=${date}`);
    return response.data;
  },

  // Staff Products (with categories)
  async getStaffProducts() {
    const response = await staffApi.get('/api/staff/products');
    return response.data;
  },

  // ============== Delivery ==============
  
  // Get orders ready for delivery
  async getOrdersReadyForDelivery(date = null) {
    const params = date ? `?date=${date}` : '';
    const response = await staffApi.get(`/api/staff/delivery/orders${params}`);
    return response.data;
  },

  // Create a delivery route
  async createDeliveryRoute(data) {
    const response = await staffApi.post('/api/staff/delivery/routes', data);
    return response.data;
  },

  // Get delivery routes
  async getDeliveryRoutes(filters = {}) {
    const params = new URLSearchParams();
    if (filters.date) params.append('date', filters.date);
    if (filters.status) params.append('status', filters.status);
    const response = await staffApi.get(`/api/staff/delivery/routes?${params}`);
    return response.data;
  },

  // Get single route detail
  async getDeliveryRoute(routeId) {
    const response = await staffApi.get(`/api/staff/delivery/routes/${routeId}`);
    return response.data;
  },

  // Start a delivery route
  async startDeliveryRoute(routeId) {
    const response = await staffApi.put(`/api/staff/delivery/routes/${routeId}/start`);
    return response.data;
  },

  // Update delivery stop
  async updateDeliveryStop(routeId, stopIndex, data) {
    const response = await staffApi.put(`/api/staff/delivery/routes/${routeId}/stops/${stopIndex}`, data);
    return response.data;
  },

  // Delete delivery route
  async deleteDeliveryRoute(routeId) {
    const response = await staffApi.delete(`/api/staff/delivery/routes/${routeId}`);
    return response.data;
  },

  // Reorder delivery stops
  async reorderDeliveryStops(routeId, stopOrder) {
    const response = await staffApi.put(`/api/staff/delivery/routes/${routeId}/reorder`, stopOrder);
    return response.data;
  },

  // ===== Order verification (photos + pickup reconciliation) =====
  async addOrderPhoto(orderId, kind, payload) {
    const response = await staffApi.post(`/api/staff/orders/${orderId}/${kind}-photos`, payload);
    return response.data;
  },

  async removeOrderPhoto(orderId, kind, publicId) {
    const response = await staffApi.delete(`/api/staff/orders/${orderId}/${kind}-photos/${publicId}`);
    return response.data;
  },

  async getOrderVerification(orderId) {
    const response = await staffApi.get(`/api/staff/orders/${orderId}/verification`);
    return response.data;
  },

  async submitPickupReconciliation(orderId, payload) {
    const response = await staffApi.post(`/api/staff/orders/${orderId}/pickup-reconciliation`, payload);
    return response.data;
  },

  // Menu Calendar (read-only for kitchen prep)
  async getMenuCalendarRange(start, end) {
    const response = await staffApi.get('/api/staff/menu-calendar', { params: { start, end } });
    return response.data;
  },

  async getMenuCalendarUpcoming(days = 14) {
    const response = await staffApi.get('/api/staff/menu-calendar/upcoming', { params: { days } });
    return response.data;
  },

  async getMenuCalendarDay(dateStr) {
    const response = await staffApi.get(`/api/staff/menu-calendar/${dateStr}`);
    return response.data;
  },

  // My schedule (view + confirm only)
  async getMySchedule(weekStart) {
    const response = await staffApi.get('/api/staff/schedule/week', {
      params: weekStart ? { week_start: weekStart } : {},
    });
    return response.data;
  },

  async getMyScheduleSummary() {
    const response = await staffApi.get('/api/staff/schedule/me');
    return response.data;
  },

  async confirmShift(shiftId) {
    const response = await staffApi.post(`/api/staff/schedule/shifts/${shiftId}/confirm`);
    return response.data;
  },

  async confirmScheduleWeek(weekStart) {
    const response = await staffApi.post('/api/staff/schedule/confirm-week', { week_start: weekStart });
    return response.data;
  },

  // Change requests — time off, dropping a shift, swapping with a colleague
  async getMyShiftRequests() {
    const response = await staffApi.get('/api/staff/schedule/requests');
    return response.data;
  },

  async raiseShiftRequest(payload) {
    const response = await staffApi.post('/api/staff/schedule/requests', payload);
    return response.data;
  },

  async respondToSwap(requestId, accept) {
    const response = await staffApi.post(
      `/api/staff/schedule/requests/${requestId}/respond`, { accept },
    );
    return response.data;
  },

  async withdrawShiftRequest(requestId) {
    const response = await staffApi.post(`/api/staff/schedule/requests/${requestId}/withdraw`);
    return response.data;
  },

  async getSwapCandidates(shiftId) {
    const response = await staffApi.get('/api/staff/schedule/colleagues', {
      params: { shift_id: shiftId },
    });
    return response.data;
  },

  async getScheduleCoverage(start, end) {
    const response = await staffApi.get('/api/staff/schedule/coverage', {
      params: { start, end },
    });
    return response.data;
  }
};

export default staffService;
